import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash, FilePdf, CaretDown, CaretUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  Draft: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  Issued: 'bg-blue-50 text-blue-700 border-blue-300',
  'Partially Delivered': 'bg-amber-50 text-amber-700 border-amber-300',
  Completed: 'bg-green-50 text-green-700 border-green-300',
};

const STATUS_FLOW = ['Draft', 'Issued', 'Partially Delivered', 'Completed'];

export const PurchaseOrders = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedPO, setExpandedPO] = useState(null);

  const [form, setForm] = useState({
    supplier_id: '', quote_id: '', delivery_schedule: '', payment_terms: '', notes: '', terms_and_conditions: '',
  });
  const [lineItems, setLineItems] = useState([{ description: '', unit: '', quantity: 0, rate: 0 }]);

  const fetchData = useCallback(async () => {
    try {
      const [poRes, supplierRes, quoteRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/purchase-orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors?entity_type=supplier`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPurchaseOrders(poRes.data);
      setSuppliers(supplierRes.data);
      setQuotes(quoteRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ supplier_id: '', quote_id: '', delivery_schedule: '', payment_terms: '', notes: '', terms_and_conditions: '' });
    setLineItems([{ description: '', unit: '', quantity: 0, rate: 0 }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { toast.error('Select a supplier'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/purchase-orders`, {
        ...form, project_id: projectId, line_items: lineItems,
      }, { withCredentials: true });
      toast.success('Purchase Order created');
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create PO'); }
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-orders/${poId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleDelete = async (poId) => {
    if (!window.confirm('Delete this Purchase Order?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-orders/${poId}`, { withCredentials: true });
      toast.success('Purchase Order deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleExportPdf = async (poId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders/${poId}/export/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${poId}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Failed to export PDF'); }
  };

  const addLineItem = () => setLineItems([...lineItems, { description: '', unit: '', quantity: 0, rate: 0 }]);
  const removeLineItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx][field] = value;
    setLineItems(updated);
  };

  const lineItemsTotal = lineItems.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0), 0);
  const totalPoValue = purchaseOrders.reduce((s, po) => s + po.total_amount, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="po-page-title">Purchase Orders</h1>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Issue Purchase Orders to suppliers for material procurement. Track delivery status and manage supplier-wise ordering." />

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Total POs</span><p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="po-count">{purchaseOrders.length}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Total Value</span><p className="text-2xl font-bold text-green-700 mt-1">₹{totalPoValue.toLocaleString('en-IN')}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Pending Delivery</span><p className="text-2xl font-bold text-amber-600 mt-1">{purchaseOrders.filter(p => ['Issued', 'Partially Delivered'].includes(p.status)).length}</p></div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-zinc-800">All Purchase Orders</h2>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-green-700 hover:bg-green-800 text-white rounded-none" data-testid="create-po-button">
                <Plus size={18} className="mr-1.5" /> Create Purchase Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-xl font-bold">New Purchase Order</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Supplier *</Label>
                    <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} required
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-supplier-select">
                      <option value="">Select Supplier</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.legal_name} ({s.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Linked Quote</Label>
                    <select value={form.quote_id} onChange={(e) => setForm({ ...form, quote_id: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-quote-select">
                      <option value="">Select Quote</option>
                      {quotes.map(q => <option key={q.id} value={q.id}>{q.pi_serial || q.id} — {q.status}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Delivery Schedule</Label>
                  <textarea value={form.delivery_schedule} onChange={(e) => setForm({ ...form, delivery_schedule: e.target.value })}
                    rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-delivery"
                    placeholder="e.g., Phase 1: 50% within 7 days, Phase 2: Balance within 15 days" />
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold uppercase text-zinc-500">Items</Label>
                    <Button type="button" onClick={addLineItem} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid="po-add-item">
                      <Plus size={14} className="mr-1" /> Add Item
                    </Button>
                  </div>
                  {lineItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                      <div className="col-span-4"><Input placeholder="Description" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input placeholder="Unit" value={item.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateLineItem(idx, 'rate', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-1 text-xs text-right pt-2 font-medium">₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toLocaleString('en-IN')}</div>
                      <div className="col-span-1"><Button type="button" onClick={() => removeLineItem(idx)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-9 px-2"><Trash size={14} /></Button></div>
                    </div>
                  ))}
                  <p className="text-sm font-bold text-right mt-1">Total: ₹{lineItemsTotal.toLocaleString('en-IN')}</p>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Payment Terms</Label>
                  <textarea value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                    rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-payment" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Notes</Label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-notes" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Terms & Conditions (editable)</Label>
                  <textarea value={form.terms_and_conditions} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                    rows={4} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="po-terms"
                    placeholder="Standard T&C will be applied if left blank" />
                </div>

                <Button type="submit" className="w-full bg-green-700 hover:bg-green-800 text-white rounded-none py-5" data-testid="po-submit">Create Purchase Order</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Purchase Order List */}
        {purchaseOrders.length === 0 ? (
          <EmptyState
            title="No Purchase Orders created yet"
            description="Create a Purchase Order to procure materials from suppliers. Link it to a quote for better cost tracking."
            testId="po-empty-state"
          />
        ) : (
          <div className="space-y-3">
            {purchaseOrders.map(po => {
              const isExpanded = expandedPO === po.id;
              return (
                <div key={po.id} className="bg-white border border-zinc-200" data-testid={`po-card-${po.id}`}>
                  <div className="p-4 flex items-start justify-between cursor-pointer" onClick={() => setExpandedPO(isExpanded ? null : po.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-mono bg-zinc-100 text-zinc-600 px-2 py-0.5 border border-zinc-200" data-testid={`po-number-${po.id}`}>{po.po_number}</span>
                        <span className={`text-xs px-2 py-0.5 border ${STATUS_COLORS[po.status]}`}>{po.status}</span>
                        <span className="text-sm font-semibold text-zinc-800">{po.supplier_name}</span>
                      </div>
                      <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                        <span>₹{po.total_amount.toLocaleString('en-IN')}</span>
                        <span>{po.line_items.length} items</span>
                        {po.delivery_schedule && <span className="truncate max-w-[200px]">{po.delivery_schedule}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button onClick={(e) => { e.stopPropagation(); handleExportPdf(po.id); }} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid={`po-pdf-${po.id}`}>
                        <FilePdf size={14} className="mr-1" /> PDF
                      </Button>
                      {isExpanded ? <CaretUp size={18} className="text-zinc-400" /> : <CaretDown size={18} className="text-zinc-400" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-zinc-200 p-4 bg-zinc-50 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="font-bold uppercase text-zinc-500">Supplier Code</span><p className="text-zinc-800 mt-0.5">{po.supplier_code}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Delivery</span><p className="text-zinc-800 mt-0.5">{po.delivery_schedule || 'As per schedule'}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Project</span><p className="text-zinc-800 mt-0.5">{po.project_pi}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Created</span><p className="text-zinc-800 mt-0.5">{new Date(po.created_at).toLocaleDateString()}</p></div>
                      </div>
                      {po.line_items.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Items</h4>
                          <table className="w-full text-xs border-collapse bg-white border border-zinc-200">
                            <thead className="bg-zinc-100"><tr>{['Description', 'Unit', 'Qty', 'Rate', 'Amount'].map(h => <th key={h} className="px-3 py-2 text-left font-bold uppercase text-zinc-500 border border-zinc-200">{h}</th>)}</tr></thead>
                            <tbody>
                              {po.line_items.map((li, i) => (
                                <tr key={i} className="border border-zinc-200">
                                  <td className="px-3 py-2">{li.description}</td><td className="px-3 py-2">{li.unit}</td>
                                  <td className="px-3 py-2">{li.quantity}</td><td className="px-3 py-2">₹{parseFloat(li.rate || 0).toLocaleString('en-IN')}</td>
                                  <td className="px-3 py-2 font-medium">₹{(parseFloat(li.quantity || 0) * parseFloat(li.rate || 0)).toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {po.payment_terms && <div><h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Payment Terms</h4><p className="text-xs text-zinc-700">{po.payment_terms}</p></div>}
                      {po.notes && <div><h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Notes</h4><p className="text-xs text-zinc-700">{po.notes}</p></div>}
                      <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
                        <Label className="text-xs font-bold uppercase text-zinc-500">Status:</Label>
                        <select value={po.status} onChange={(e) => handleStatusChange(po.id, e.target.value)}
                          className="bg-white border border-zinc-200 rounded-none p-1.5 text-xs" data-testid={`po-status-${po.id}`}>
                          {STATUS_FLOW.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {po.status === 'Draft' && (
                          <Button onClick={() => handleDelete(po.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-7 text-xs ml-auto" data-testid={`po-delete-${po.id}`}>
                            <Trash size={14} className="mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PurchaseOrders;
